import { useState, useRef } from "react";
import { ChevronDown, ChevronUp, Plus, X } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { ACTION_STYLE, ATLAS_FINALISED_LABEL, NO_FEE_ACTIONS, PREPARATION_FEE_OPTIONS, actionOptionsFor, billingAppearanceType, billingExtraFees, billingFeeFor, isAtlasFinalised, isClaimedAction, isPreparationFee, lookupBillingFee } from "@/lib/billingConstants";
import { BILLING_FEE_TABLE, feeKey, findBillingFee, isPerKilometre } from "@/lib/billingFeeTable";
import EmailModal from "./EmailModal";
import AidStatusStrip from "./AidStatusStrip";

export default function AppearanceRow({ entry, action, note = "", fee = {}, aid, aidSuggestion, onActionChange, onNoteChange, onFeeChange, onAidChange, onUseAidSuggestion }) {
  const [expanded, setExpanded] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [acfFile, setAcfFile] = useState(null); // { name, url }
  const [acfUploading, setAcfUploading] = useState(false);
  const acfInputRef = useRef();
  const appearanceType = billingAppearanceType(entry, action);
  const noFee = NO_FEE_ACTIONS.includes(action);
  const listedFee = noFee ? null : lookupBillingFee({ court: entry.court, appearanceType, preparationType: fee.preparationType });
  // A typed amount overrides the listed fee; clearing the box goes back to the listed fee.
  const feeValue = noFee ? "" : fee.amount ?? (listedFee != null ? String(listedFee) : "");

  // Extra fee lines on this entry. The stored lines keep what was typed; `extraFees` is the
  // same lines with their listed rate and worked-out amount.
  const storedExtra = Array.isArray(fee.extra) ? fee.extra : [];
  const extraFees = billingExtraFees(fee);
  const total = billingFeeFor(entry, action, fee);
  // Claimed, but nothing to claim: flagged so it is fixed before the sheet is printed.
  const claimedWithoutFee = isClaimedAction(action) && total == null;
  const setExtra = (next) => onFeeChange?.(entry.id, { extra: next });
  const addExtra = () => setExtra([...storedExtra, { id: `fee-${Date.now()}-${storedExtra.length}`, feeKey: "", quantity: 1 }]);
  const updateExtra = (id, patch) => setExtra(storedExtra.map(line => (line.id === id ? { ...line, ...patch } : line)));
  const removeExtra = (id) => setExtra(storedExtra.filter(line => line.id !== id));
  
  const handleActionSelect = (value) => {
    if (value === "Aid required" || value === "Extension required") {
      setPendingAction(value);
      setShowEmailModal(true);
    }
    if (value === "ACF lodged") {
      setExpanded(true);
    }
    onActionChange(entry.id, value);
  };

  const handleEmailClose = () => {
    setShowEmailModal(false);
    setPendingAction(null);
  };

  const handleAcfUpload = async (file) => {
    if (!file) return;
    setAcfUploading(true);
    const { file_url } = await base44.integrations.Core.UploadFile({ file });
    setAcfFile({ name: file.name, url: file_url });
    setAcfUploading(false);
    if (acfInputRef.current) acfInputRef.current.value = "";
  };

  return (
    <>
      {showEmailModal && (
        <EmailModal
          entry={entry}
          actionType={pendingAction}
          onClose={handleEmailClose}
        />
      )}
      <div id={`appearance-${entry.id}`} className={`rounded-lg border bg-white overflow-hidden scroll-mt-24 ${claimedWithoutFee ? "border-amber-400 ring-2 ring-amber-200" : "border-slate-200"}`}>
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full text-left px-3 py-2.5 flex items-start gap-3 hover:bg-slate-50 transition-colors"
        >
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="text-sm font-bold text-slate-900">{entry.date || "—"}</span>
              {entry.client_name && <span className="text-sm font-semibold text-slate-700">{entry.client_name}</span>}
              {entry.grant_type && (
                <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-bold text-violet-800">
                  {entry.grant_type}
                </span>
              )}
              {entry.lawyer_initials && (
                <span className="text-xs font-semibold text-slate-500">Carriage: {entry.lawyer_initials}</span>
              )}
              {entry.court && <span className="text-xs text-slate-500">{entry.court}</span>}
              {claimedWithoutFee && (
                <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-800">Claimed, no fee allocated</span>
              )}
              {isAtlasFinalised(fee) && (
                <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-800">Grant finalised on ATLAS</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <select
              value={action || ""}
              onChange={e => handleActionSelect(e.target.value)}
              className={`text-xs font-semibold px-2 py-1 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 cursor-pointer ${
                action ? ACTION_STYLE[action] : "bg-slate-50 text-slate-400 border-slate-200"
              }`}
            >
              {actionOptionsFor(action).map(opt => (
                <option key={opt.value} value={opt.value} disabled={opt.disabled}>{opt.label}</option>
              ))}
            </select>
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </button>
        {aid && (
          <AidStatusStrip
            entryId={entry.id}
            aid={aid}
            suggestion={aidSuggestion}
            action={action}
            onAidChange={onAidChange}
            onUseSuggestion={onUseAidSuggestion}
          />
        )}
        <div className="flex flex-col gap-3 border-t border-slate-100 bg-white px-3 py-2.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-2">
            <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={isAtlasFinalised(fee)}
                onChange={e => onFeeChange?.(entry.id, { atlasFinalised: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 accent-emerald-600"
              />
              {ATLAS_FINALISED_LABEL}
            </label>
            <div className="flex flex-wrap items-end gap-2">
            {appearanceType === "Preparation" && (
              <div>
                <label htmlFor={`prep-type-${entry.id}`} className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">
                  Preparation fee
                </label>
                <select
                  id={`prep-type-${entry.id}`}
                  value={fee.preparationType || ""}
                  onChange={e => onFeeChange?.(entry.id, { preparationType: e.target.value, amount: undefined })}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                >
                  <option value="">Choose…</option>
                  {PREPARATION_FEE_OPTIONS.map(option => (
                    <option key={option} value={option}>{option.replace("Lump sum ", "")}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label htmlFor={`fee-${entry.id}`} className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">
                Fee{appearanceType ? ` · ${appearanceType}` : ""}
              </label>
              <div className="flex items-center rounded-lg border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-purple-300">
                <span className="pl-3 text-sm text-slate-400">$</span>
                <input
                  id={`fee-${entry.id}`}
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={feeValue}
                  disabled={noFee}
                  onChange={e => onFeeChange?.(entry.id, { amount: e.target.value === "" ? undefined : e.target.value })}
                  placeholder={noFee ? "No fee" : appearanceType === "Preparation" && !fee.preparationType ? "Choose fee" : "No listed fee"}
                  className="w-28 rounded-lg bg-transparent px-2 py-2 text-sm focus:outline-none"
                />
              </div>
              {fee.amount != null && listedFee != null && Number(fee.amount) !== listedFee && (
                <p className="mt-1 text-xs text-amber-700">Listed fee ${listedFee}</p>
              )}
            </div>
            </div>

            {/* More fees for the same diary entry: preparation as well as the appearance,
                travel, an uplift. Each is picked from the VLA fee table. */}
            {extraFees.map((line) => {
              const listed = findBillingFee(line.feeKey);
              const stored = storedExtra.find(item => item.id === line.id) || {};
              return (
                <div key={line.id} className="flex flex-wrap items-end gap-2">
                  <div className="min-w-0">
                    <label htmlFor={`extra-fee-${line.id}`} className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">Additional fee</label>
                    <select
                      id={`extra-fee-${line.id}`}
                      value={line.feeKey}
                      onChange={e => updateExtra(line.id, { feeKey: e.target.value, amount: undefined })}
                      className="w-64 max-w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    >
                      <option value="">Choose a fee…</option>
                      {Object.entries(BILLING_FEE_TABLE).map(([table, rows]) => (
                        <optgroup key={table} label={table}>
                          {rows.map(([name, amount]) => (
                            <option key={name} value={feeKey(table, name)}>{name} – ${amount}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor={`extra-qty-${line.id}`} className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">{isPerKilometre(listed) ? "Km" : "Qty"}</label>
                    <input
                      id={`extra-qty-${line.id}`}
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      value={stored.quantity ?? 1}
                      onChange={e => updateExtra(line.id, { quantity: e.target.value, amount: undefined })}
                      className="w-16 rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    />
                  </div>
                  {/* The date this fee is for. A preparation fee has none. */}
                  {listed && !isPreparationFee(listed.name) && (
                    <div>
                      <label htmlFor={`extra-date-${line.id}`} className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">Date</label>
                      <input
                        id={`extra-date-${line.id}`}
                        type="date"
                        value={stored.date || ""}
                        onChange={e => updateExtra(line.id, { date: e.target.value || undefined })}
                        className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                      />
                    </div>
                  )}
                  <div>
                    <label htmlFor={`extra-amount-${line.id}`} className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">Amount</label>
                    <div className="flex items-center rounded-lg border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-purple-300">
                      <span className="pl-3 text-sm text-slate-400">$</span>
                      <input
                        id={`extra-amount-${line.id}`}
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={stored.amount ?? (line.listedAmount != null ? String(line.listedAmount) : "")}
                        onChange={e => updateExtra(line.id, { amount: e.target.value === "" ? undefined : e.target.value })}
                        placeholder="Choose fee"
                        className="w-24 rounded-lg bg-transparent px-2 py-2 text-sm focus:outline-none"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeExtra(line.id)}
                    aria-label="Remove this fee"
                    className="mb-1 rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={addExtra}
                className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:text-purple-900"
              >
                <Plus className="h-3.5 w-3.5" /> Add fee
              </button>
              {extraFees.length > 0 && total != null && (
                <span className="text-sm font-semibold text-slate-800">Total ${total.toFixed(2)}</span>
              )}
            </div>
          </div>
          <div className="w-full sm:w-80">
            <label htmlFor={`manual-entry-${entry.id}`} className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">
              Notes
            </label>
            <textarea
              id={`manual-entry-${entry.id}`}
              value={note}
              onChange={e => onNoteChange?.(entry.id, e.target.value)}
              placeholder="Add a note about this appearance..."
              rows={2}
              className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            />
          </div>
        </div>
        {expanded && (
          <div className="px-3 pb-3 pt-2 border-t border-slate-100 space-y-3 bg-slate-50">
            <div className="space-y-2">
              {entry.outcome && <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-0.5">Outcome</p><p className="text-sm text-slate-700">{entry.outcome}</p></div>}
              {entry.next_date && <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-0.5">Next Date</p><p className="text-sm text-slate-700">{entry.next_date}</p></div>}
              {entry.counsel_briefed && <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-0.5">Counsel</p><p className="text-sm text-slate-700">{entry.counsel_briefed}</p></div>}
              {entry.notes && <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-0.5">Diary entry</p><p className="text-sm text-slate-700">{entry.notes}</p></div>}
            </div>
            
          </div>
        )}
      </div>
    </>
  );
}
